import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/styles";
import TableHeader from "../../../components/table-header";
import FilterModal from "../../../components/filterModal";
import StrategyBadge from "./StrategyBadge";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "1px solid var(--border-color)",
    color: "var(--text-primary)",
    padding: "6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
    color: "var(--text-primary)",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const SKELETON_ROWS = 6;

const tableSchema = [
  { key: "sl_no", title: "SL NO" },
  { key: "name", title: "Rule Name", hasFilter: true },
  { key: "client", title: "Client", hasFilter: true },
  { key: "lot_strategy", title: "Lot Strategy", hasFilter: true },
  { key: "warehouse_strategy", title: "Warehouse Strategy", hasFilter: true },
  { key: "split_order", title: "Split Order" },
  { key: "is_active", title: "Status", hasFilter: true },
  { key: "action", title: "" },
];

export default function AdminView({
  rules,
  loading,
  onEdit,
  onDelete,
  currentPage,
  itemsPerPage,
  filterData,
  onChangeFilter,
}) {
  const theme = useTheme();

  const [anchorElAction, setAnchorElAction] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [selectedFilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    options: [],
    displayKey: "label",
    id: "value",
    loadMoreButton: false,
    fetchApi: null,
    searchKey: "",
  });

  const startIndex = currentPage * itemsPerPage;

  const handleMenuOpen = (e, rule) => {
    e.stopPropagation();
    setAnchorElAction(e.currentTarget);
    setSelectedRule(rule);
  };
  const handleMenuClose = () => {
    setAnchorElAction(null);
    setSelectedRule(null);
  };

  const handleOpenFilter = (e, filterKey) => {
    setAnchorElFilter(e.currentTarget);
    setOpenFilterModal(true);
    const col = filterData?.columns?.find((x) => x.key === filterKey);
    if (col) setSelectedFilterData(col);
  };
  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };
  const handleSaveFilter = (selectedOptions, filterKey, sortOrder, searchKey) => {
    onChangeFilter({ key: filterKey, filter: selectedOptions, sort: sortOrder, searchKey });
    handleCloseFilter();
  };

  const schemaWithFilter = tableSchema.map((col) =>
    col.hasFilter ? { ...col, handleOpenFilter } : col
  );

  return (
    <div
      style={{
        overflowY: "scroll",
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 200px)",
        width: "100%",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        paddingBottom: 20,
      }}
    >
      <TableContainer sx={{ overflowY: "auto", paddingInline: 0.4, maxHeight: "100%" }}>
        <Table stickyHeader aria-label="fulfillment rules table" sx={{ border: "1px solid var(--border-color)" }}>
          <TableHead>
            <StyledTableRow>
              {schemaWithFilter.map((col) => (
                <TableHeader
                  key={col.key}
                  title={col.title}
                  filterData={filterData}
                  filterKey={col.key}
                  handleOpenFilter={col.handleOpenFilter}
                />
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <StyledTableRow key={i}>
                    <StyledTableCell><Skeleton width={20} /></StyledTableCell>
                    <StyledTableCell>
                      <Skeleton width="80%" />
                      <Skeleton width="50%" height={10} />
                    </StyledTableCell>
                    <StyledTableCell><Skeleton width="70%" /></StyledTableCell>
                    <StyledTableCell><Skeleton variant="rounded" width={50} height={20} /></StyledTableCell>
                    <StyledTableCell><Skeleton variant="rounded" width={80} height={20} /></StyledTableCell>
                    <StyledTableCell><Skeleton variant="rounded" width={40} height={20} /></StyledTableCell>
                    <StyledTableCell><Skeleton variant="rounded" width={50} height={20} /></StyledTableCell>
                    <StyledTableCell><Skeleton variant="circular" width={24} height={24} /></StyledTableCell>
                  </StyledTableRow>
                ))
              : rules.map((rule, idx) => (
                  <StyledTableRow key={rule._id}>
                    <StyledTableCell>
                      <Typography fontSize={12} color="var(--text-secondary)" fontFamily="Poppins">
                        {startIndex + idx + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography fontSize={13} fontWeight={600} color="var(--text-tertiary)" fontFamily="Poppins">
                        {rule.name}
                      </Typography>
                      {rule.description && (
                        <Typography fontSize={11} color="var(--text-secondary)" fontFamily="Poppins">
                          {rule.description}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography fontSize={12} color="var(--text-secondary)" fontFamily="Poppins">
                        {rule.client?.business_name || rule.client?.name || "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <StrategyBadge value={rule.lot_strategy} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <StrategyBadge value={rule.warehouse_strategy} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={rule.split_order ? "Yes" : "No"}
                        size="small"
                        variant="outlined"
                        color={rule.split_order ? "primary" : "default"}
                        sx={{ fontFamily: "Poppins", fontSize: 10 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={rule.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={rule.is_active ? "success" : "default"}
                        sx={{ fontFamily: "Poppins", fontSize: 10 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, rule)}
                        sx={{ color: "var(--text-secondary)" }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action popover */}
      <Popover
        open={Boolean(anchorElAction)}
        anchorEl={anchorElAction}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "none",
            border: "1px solid var(--border-color)",
            borderRadius: "4px",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", p: 1 }}>
          <Button
            startIcon={<EditIcon />}
            onClick={() => { onEdit(selectedRule); handleMenuClose(); }}
            sx={{ justifyContent: "flex-start", textTransform: "none", color: "var(--text-tertiary)", fontFamily: "Poppins", fontSize: 14 }}
          >
            Edit
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            onClick={() => { onDelete(selectedRule); handleMenuClose(); }}
            sx={{ justifyContent: "flex-start", textTransform: "none", color: "error.main", fontFamily: "Poppins", fontSize: 14 }}
          >
            Delete
          </Button>
        </Box>
      </Popover>

      {/* Column filter popover */}
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedFilterData?.options}
          selectedValues={selectedFilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedFilterData?.sort}
          filterKey={selectedFilterData?.key}
          hasMore={selectedFilterData?.loadMoreButton}
          displayKey={selectedFilterData?.displayKey}
          id={selectedFilterData?.id}
          fetchApi={selectedFilterData?.fetchApi}
          searchKey={selectedFilterData?.searchKey}
        />
      </Popover>
    </div>
  );
}
